"use client";

import React, { useRef, useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import Image from "next/image";
import DecryptedText from "@/components/ui/DecryptedText";
import { useGSAP } from "@gsap/react";
import {
  ArrowRight,
  LayoutDashboard,
  ShieldAlert,
  CreditCard,
  Megaphone,
  Users,
  Settings,
  LogOut,
  ChevronDown,
  Plus,
  IndianRupee,
  AlertTriangle,
  Ticket,
  Package,
  Car,
  User,
  CheckCircle2,
  Timer,
  TrendingUp,
  Square,
  X,
  Loader2,
  Smartphone,
  Mail,
  MapPin,
} from "lucide-react";

// Imports from your project structure
import DotGrid from "@/components/DotGrid";
import { ContainerScroll } from "@/components/ui/ContainerScroll";

const HeroSection = () => {
  const containerRef = useRef(null);

  // --- MODAL STATE ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    interest: "RWA Solutions",
  });

  // --- FORM SUBMIT HANDLER ---
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      alert("Thanks! We will contact you shortly.");
      setIsModalOpen(false);
      setFormData({
        name: "",
        mobile: "",
        email: "",
        city: "",
        interest: "RWA Solutions",
      });
    }, 2000);
  };

  useGSAP(
    () => {
      // Reveal elements before animating — they start with visibility: hidden via CSS
      gsap.set(
        ".hero-badge, .hero-title-line, .hero-desc, .hero-btn, .hero-card-3d",
        { visibility: "visible" },
      );

      const tl = gsap.timeline();

      tl.from(".hero-badge", {
        y: -20,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
      })
        .from(
          ".hero-title-line",
          {
            y: 50,
            opacity: 0,
            stagger: 0.2,
            duration: 1,
            ease: "power4.out",
          },
          "-=0.4",
        )
        .from(
          ".hero-desc",
          {
            y: 20,
            opacity: 0,
            duration: 0.8,
          },
          "-=0.6",
        )
        .fromTo(
          ".hero-btn",
          { scale: 0.8, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            stagger: 0.1,
            duration: 0.6,
            ease: "back.out(1.7)",
          },
          "-=0.4",
        )
        .from(
          ".hero-card-3d",
          {
            y: 100,
            opacity: 0,
            duration: 1.2,
            ease: "power2.out",
          },
          "-=0.8",
        );
    },
    { scope: containerRef },
  );

  return (
    <section
      ref={containerRef}
      className="relative w-full bg-[#050505] text-white overflow-hidden pt-60 md:pt-20"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--tw-gradient-stops))] from-orange-900/10 via-[#050505] to-[#050505] z-0" />
      <div className="absolute inset-0 bg-[url('/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay pointer-events-none z-0"></div>

      {/* DotGrid Background */}
      <div className="absolute inset-0 z-0 overflow-hidden opacity-40">
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
          <DotGrid
            dotSize={5}
            gap={15}
            baseColor="#202020"
            activeColor="#f97316"
            proximity={120}
            speedTrigger={100}
            shockRadius={250}
            shockStrength={5}
            maxSpeed={5000}
            resistance={750}
            returnDuration={1.5}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>

      <div className="relative z-10">
        <ContainerScroll
          titleComponent={
            <>
              <div>
                <span className="hero-badge invisible px-3 py-1.5 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs font-bold uppercase tracking-widest mb-8 inline-block backdrop-blur-md">
                  v1.0 is Live • The Future of Gate Security
                </span>

                {/* Primary H1: Visible, crawlable, single instance */}
                <h1 className="hero-title-line invisible text-s md:text-xl font-clash font-semibold uppercase tracking-wider text-gray-300 mb-4">
                  Privacy-First Society OS — SmartDwara
                </h1>

                {/* Marketing tagline as h2 for visual hierarchy */}
                <p className="text-5xl md:text-8xl font-clash font-bold tracking-tight text-white mb-8 leading-tight">
                  <span className="hero-title-line invisible block">
                    The Gatekeeper that
                  </span>
                  <span className="hero-title-line invisible block">
                    <DecryptedText
                      text="Never Sleeps."
                      animateOn="view"
                      speed={60}
                      maxIterations={2.5}
                      sequential={true}
                      revealDirection="start"
                      className="text-transparent bg-clip-text bg-linear-to-r from-orange-500 to-amber-500"
                      parentClassName="text-transparent bg-clip-text bg-linear-to-r from-orange-500 to-amber-500"
                    />
                  </span>
                </p>

                <p className="hero-desc invisible text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                  Smart Dwara brings military-grade security to your doorstep.
                  Manage your home, office, or society with the speed of a text
                  message and the security of a bank vault.
                </p>

                <div className="flex flex-col sm:flex-row gap-5 justify-center items-center mb-20">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="hero-btn invisible px-10 py-4 bg-[#F97316] hover:bg-orange-600 text-white rounded-full font-clash font-bold uppercase text-xl tracking-wider transition-all shadow-[0_0_30px_rgba(249,115,22,0.3)] hover:shadow-[0_0_50px_rgba(249,115,22,0.5)] flex items-center gap-2 active:scale-95"
                  >
                    Get Protected <ArrowRight className="w-5 h-5" />
                  </button>

                  <Link
                    href="/demo"
                    className="hero-btn invisible px-10 py-4 bg-[#111] hover:bg-[#1a1a1a] text-orange-300 hover:text-white border border-white/10 hover:border-orange-500 rounded-full font-clash font-semibold text-xl tracking-wider uppercase transition-all active:scale-95"
                  >
                    Interactive Demo
                  </Link>
                </div>
              </div>
            </>
          }
        >
          {/* --- HIGH-FIDELITY SOCIETY ADMIN DASHBOARD REPLICA --- */}
          <div className="dashboard-ui w-full h-auto md:h-137.5 bg-[#050505] rounded-3xl border border-white/10 flex text-left shadow-2xl overflow-hidden">
            {/* === SIDEBAR "The Spine" === */}
            <div className="flex w-12 md:w-56 border-r border-white/5 bg-[#0A0A0A] flex-col z-20 shrink-0">
              {/* User Profile Card */}
              <div className="p-1.5 md:p-3 border-b border-white/5">
                <div className="flex items-center gap-1.5 md:gap-2.5 p-1.5 md:p-2.5 bg-[#121212] rounded-lg md:rounded-xl border border-white/5">
                  <div className="w-5 h-5 md:w-8 md:h-8 bg-orange-500 rounded-md md:rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(249,115,22,0.5)]">
                    <Image
                      src="/brand/logo.svg"
                      alt="SmartDwara Logo"
                      width={32}
                      height={32}
                      className="w-4 h-4 md:w-8 md:h-8 rounded-sm md:rounded-xl"
                    />
                  </div>
                  <div className="hidden md:block flex-1 min-w-0">
                    <p className="text-white text-[10px] font-bold truncate">
                      SmartDwara Society
                    </p>
                    <div className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border bg-orange-500/10 text-orange-400 border-orange-500/20 text-[7px] font-black uppercase tracking-wider mt-0.5">
                      Admin
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Groups */}
              <nav className="flex-1 px-1 md:px-2 py-2 md:py-3 space-y-2 md:space-y-4 overflow-hidden">
                {/* Operations Group */}
                <div>
                  <p className="hidden md:block text-gray-600 text-[7px] font-black uppercase tracking-[0.15em] px-2 mb-1">
                    Operations
                  </p>
                  <div className="space-y-0.5 md:space-y-1">
                    {/* Dashboard - Active */}
                    <div className="relative flex items-center gap-1.5 md:gap-2.5 px-1.5 md:px-2.5 py-1.5 md:py-2 rounded-md md:rounded-lg bg-white/5 text-white">
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 md:w-1 h-3 md:h-5 bg-orange-500 rounded-r-full shadow-[0_0_6px_rgba(249,115,22,0.5)]" />
                      <LayoutDashboard
                        size={10}
                        className="text-orange-500 md:w-4 md:h-4"
                      />
                      <span className="hidden md:block text-[9px] font-bold tracking-wide">
                        Dashboard
                      </span>
                      <div className="hidden md:block ml-auto w-1 h-1 rounded-full bg-orange-500 animate-pulse shadow-[0_0_4px_rgba(249,115,22,0.8)]" />
                    </div>
                    {/* Gate Logs */}
                    <div className="flex items-center gap-1.5 md:gap-2.5 px-1.5 md:px-2.5 py-1.5 md:py-2 rounded-md md:rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors">
                      <ShieldAlert
                        size={10}
                        className="text-gray-600 md:w-4 md:h-4"
                      />
                      <span className="hidden md:block text-[9px] font-bold tracking-wide">
                        Gate Logs
                      </span>
                    </div>
                    {/* Notices */}
                    <div className="flex items-center gap-1.5 md:gap-2.5 px-1.5 md:px-2.5 py-1.5 md:py-2 rounded-md md:rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors">
                      <Megaphone
                        size={10}
                        className="text-gray-600 md:w-4 md:h-4"
                      />
                      <span className="hidden md:block text-[9px] font-bold tracking-wide">
                        Notices
                      </span>
                    </div>
                  </div>
                </div>

                {/* Finance Group */}
                <div>
                  <p className="hidden md:block text-gray-600 text-[7px] font-black uppercase tracking-[0.15em] px-2 mb-1">
                    Finance
                  </p>
                  <div className="space-y-0.5 md:space-y-1">
                    <div className="flex items-center gap-1.5 md:gap-2.5 px-1.5 md:px-2.5 py-1.5 md:py-2 rounded-md md:rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors">
                      <CreditCard
                        size={10}
                        className="text-gray-600 md:w-4 md:h-4"
                      />
                      <span className="hidden md:block text-[9px] font-bold tracking-wide">
                        Billing
                      </span>
                    </div>
                  </div>
                </div>

                {/* Admin Group */}
                <div>
                  <p className="hidden md:block text-gray-600 text-[7px] font-black uppercase tracking-[0.15em] px-2 mb-1">
                    Admin
                  </p>
                  <div className="space-y-0.5 md:space-y-1">
                    <div className="flex items-center gap-1.5 md:gap-2.5 px-1.5 md:px-2.5 py-1.5 md:py-2 rounded-md md:rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors">
                      <Users
                        size={10}
                        className="text-gray-600 md:w-4 md:h-4"
                      />
                      <span className="hidden md:block text-[9px] font-bold tracking-wide">
                        Residents
                      </span>
                    </div>
                  </div>
                </div>
              </nav>

              {/* Bottom Section */}
              <div className="px-1 md:px-2 pb-1 md:pb-2 space-y-0.5 md:space-y-1">
                <div className="flex items-center gap-1.5 md:gap-2.5 px-1.5 md:px-2.5 py-1.5 md:py-2 rounded-md md:rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors">
                  <Settings size={10} className="text-gray-600 md:w-4 md:h-4" />
                  <span className="hidden md:block text-[9px] font-bold tracking-wide">
                    Settings
                  </span>
                </div>
              </div>
              <div className="p-1 md:p-2 border-t border-white/5 bg-[#080808]">
                <div className="flex items-center justify-center gap-1 md:gap-1.5 px-2 py-1 md:py-1.5 rounded-md md:rounded-lg bg-[#1A1A1A] text-red-500 border border-white/5 text-[6px] md:text-[8px] font-bold uppercase tracking-widest">
                  <LogOut size={8} className="md:w-3 md:h-3" />
                  <span className="hidden md:inline">Sign Out</span>
                </div>
              </div>
            </div>

            {/* === MAIN CONTENT AREA === */}
            <div className="flex-1 bg-[#0A0A0A] p-2 md:p-6 flex flex-col relative overflow-hidden h-auto md:h-full">
              {/* Glossy Overlay */}
              <div className="absolute inset-0 bg-linear-to-br from-white/5 to-transparent pointer-events-none" />

              {/* === HEADER (Action-First Top Bar) === */}
              <div className="flex justify-between items-center mb-2 md:mb-6 relative z-10">
                <div>
                  <div className="flex items-center gap-1 md:gap-2 mb-0.5 md:mb-1">
                    <span className="text-gray-500 text-[6px] md:text-[10px] font-bold">
                      Galaxy Apts
                    </span>
                    <span className="text-gray-600 text-[6px] md:text-[10px]">
                      /
                    </span>
                    <span className="text-white text-[6px] md:text-[10px] font-bold">
                      Dashboard
                    </span>
                  </div>
                  <h3 className="text-white font-black text-[8px] md:text-lg tracking-tight">
                    Command <span className="text-orange-500">Center</span>
                  </h3>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  {/* Quick Actions Button */}
                  <div className="bg-orange-500 hover:bg-orange-600 text-white px-1.5 md:px-3 py-1 md:py-2 rounded-md md:rounded-lg font-bold text-[5px] md:text-[10px] uppercase tracking-wider flex items-center gap-0.5 md:gap-1.5 shadow-lg shadow-orange-500/20 cursor-pointer">
                    <Plus size={6} className="md:w-3.5 md:h-3.5" />
                    <span className="hidden md:inline">Quick Actions</span>
                    <ChevronDown size={5} className="md:w-3 md:h-3" />
                  </div>
                  {/* Live Status */}
                  <div className="flex items-center gap-1 px-1.5 md:px-3 py-1 md:py-2 bg-[#121212] rounded-full border border-white/10">
                    <span className="relative flex h-1 w-1 md:h-2 md:w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1 w-1 md:h-2 md:w-2 bg-green-500"></span>
                    </span>
                    <span className="text-[5px] md:text-[9px] font-black text-gray-300 uppercase tracking-widest">
                      Live
                    </span>
                  </div>
                </div>
              </div>

              {/* === KPI CARDS ROW === */}
              <div className="grid grid-cols-4 gap-1 md:gap-3 mb-2 md:mb-4 relative z-10">
                {/* Total Revenue */}
                <div className="bg-[#111] border border-white/5 p-1.5 md:p-3 rounded-lg md:rounded-xl relative overflow-hidden group">
                  <div className="absolute right-0 top-0 p-2 opacity-10">
                    <IndianRupee
                      size={20}
                      className="text-green-500 md:w-10 md:h-10"
                    />
                  </div>
                  <div className="flex items-center gap-0.5 mb-0.5 md:mb-1">
                    <div className="w-0.5 h-2 md:h-3 bg-green-500 rounded-full" />
                    <p className="text-[5px] md:text-[8px] uppercase tracking-wider text-gray-500 font-bold truncate">
                      Revenue
                    </p>
                  </div>
                  <p className="text-[8px] md:text-xl font-black text-white">
                    ₹4.2L
                  </p>
                  <div className="hidden md:flex items-center gap-1 mt-1">
                    <TrendingUp size={8} className="text-green-500" />
                    <span className="text-[6px] text-green-500 font-bold">
                      +12%
                    </span>
                  </div>
                </div>

                {/* Active Notices */}
                <div className="bg-[#111] border border-white/5 p-1.5 md:p-3 rounded-lg md:rounded-xl relative overflow-hidden group">
                  <div className="absolute right-0 top-0 p-2 opacity-10">
                    <Megaphone
                      size={20}
                      className="text-orange-500 md:w-10 md:h-10"
                    />
                  </div>
                  <div className="flex items-center gap-0.5 mb-0.5 md:mb-1">
                    <div className="w-0.5 h-2 md:h-3 bg-orange-500 rounded-full" />
                    <p className="text-[5px] md:text-[8px] uppercase tracking-wider text-gray-500 font-bold truncate">
                      Notices
                    </p>
                  </div>
                  <p className="text-[8px] md:text-xl font-black text-white">
                    3
                  </p>
                  <span className="hidden md:inline-flex items-center px-1 py-0.5 rounded bg-orange-500/10 border border-orange-500/20 text-[5px] text-orange-400 font-bold mt-1">
                    Active
                  </span>
                </div>

                {/* Pending Visitors */}
                <div className="bg-[#111] border border-white/5 p-1.5 md:p-3 rounded-lg md:rounded-xl relative overflow-hidden group">
                  <div className="absolute right-0 top-0 p-2 opacity-10">
                    <Users
                      size={20}
                      className="text-blue-500 md:w-10 md:h-10"
                    />
                  </div>
                  <div className="flex items-center gap-0.5 mb-0.5 md:mb-1">
                    <div className="w-0.5 h-2 md:h-3 bg-blue-500 rounded-full" />
                    <p className="text-[5px] md:text-[8px] uppercase tracking-wider text-gray-500 font-bold truncate">
                      Pending
                    </p>
                  </div>
                  <p className="text-[8px] md:text-xl font-black text-white">
                    7
                  </p>
                  <span className="hidden md:inline-flex items-center px-1 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-[5px] text-blue-400 font-bold mt-1">
                    At Gate
                  </span>
                </div>

                {/* Open Tickets */}
                <div className="bg-[#111] border border-white/5 p-1.5 md:p-3 rounded-lg md:rounded-xl relative overflow-hidden group">
                  <div className="absolute right-0 top-0 p-2 opacity-10">
                    <Ticket
                      size={20}
                      className="text-red-500 md:w-10 md:h-10"
                    />
                  </div>
                  <div className="flex items-center gap-0.5 mb-0.5 md:mb-1">
                    <div className="w-0.5 h-2 md:h-3 bg-red-500 rounded-full" />
                    <p className="text-[5px] md:text-[8px] uppercase tracking-wider text-gray-500 font-bold truncate">
                      Tickets
                    </p>
                  </div>
                  <p className="text-[8px] md:text-xl font-black text-white">
                    2
                  </p>
                  <span className="hidden md:inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-[5px] text-red-400 font-bold mt-1">
                    <AlertTriangle size={6} /> Open
                  </span>
                </div>
              </div>

              {/* === RICH LIST: RECENT GATE LOGS === */}
              <div className="flex-1 bg-[#111] rounded-lg md:rounded-2xl border border-white/5 p-1.5 md:p-4 relative z-10 mb-2 md:mb-4 overflow-hidden">
                <div className="flex items-center justify-between mb-1 md:mb-3">
                  <div className="flex items-center gap-1 md:gap-2">
                    <div className="w-0.5 h-2 md:h-3 bg-white/20 rounded-full" />
                    <h4 className="text-white font-black text-[6px] md:text-xs uppercase tracking-wider">
                      Recent Gate Logs
                    </h4>
                  </div>
                  <span className="text-orange-500 text-[5px] md:text-[9px] font-bold uppercase tracking-wider">
                    View All →
                  </span>
                </div>

                {/* Log Rows */}
                <div className="space-y-0.5 md:space-y-1">
                  {/* Row 1 */}
                  <div className="flex items-center gap-1 md:gap-3 p-1 md:p-2.5 rounded-md md:rounded-xl bg-orange-500/5 border border-orange-500/20 group">
                    {/* Checkbox */}
                    <div className="w-2.5 h-2.5 md:w-4 md:h-4 rounded-sm md:rounded-md border border-orange-500 bg-orange-500 flex items-center justify-center shrink-0">
                      <CheckCircle2
                        size={6}
                        className="text-white md:w-2.5 md:h-2.5"
                      />
                    </div>
                    {/* Avatar */}
                    <div className="w-4 h-4 md:w-8 md:h-8 rounded-md md:rounded-lg bg-[#1A1A1A] flex items-center justify-center border border-white/5 shrink-0">
                      <Package
                        size={6}
                        className="text-gray-400 md:w-4 md:h-4"
                      />
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-[6px] md:text-xs truncate">
                        Swiggy Delivery
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-gray-500 text-[5px] md:text-[8px] font-bold uppercase bg-white/5 px-1 py-0.5 rounded">
                          Delivery
                        </span>
                        <span className="text-orange-400 text-[5px] md:text-[8px] font-bold">
                          Unit 302
                        </span>
                      </div>
                    </div>
                    {/* Time & Status */}
                    <div className="flex items-center gap-1 md:gap-2 shrink-0">
                      <span className="text-gray-500 text-[5px] md:text-[8px] font-mono hidden md:inline">
                        10:42 AM
                      </span>
                      <div className="px-1 md:px-2 py-0.5 rounded-md border text-[5px] md:text-[7px] font-black uppercase tracking-wider text-green-500 bg-green-500/10 border-green-500/20 flex items-center gap-0.5">
                        <CheckCircle2 size={6} className="md:w-2 md:h-2" />{" "}
                        <span className="hidden md:inline">Approved</span>
                      </div>
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="flex items-center gap-1 md:gap-3 p-1 md:p-2.5 rounded-md md:rounded-xl hover:bg-[#1A1A1A] border border-transparent hover:border-white/5 transition-colors group">
                    <div className="w-2.5 h-2.5 md:w-4 md:h-4 rounded-sm md:rounded-md border-2 border-gray-600 group-hover:border-gray-400 transition-colors shrink-0">
                      <Square
                        size={6}
                        className="opacity-0 md:w-2.5 md:h-2.5"
                      />
                    </div>
                    <div className="w-4 h-4 md:w-8 md:h-8 rounded-md md:rounded-lg bg-[#1A1A1A] flex items-center justify-center border border-white/5 shrink-0">
                      <User size={6} className="text-gray-400 md:w-4 md:h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-[6px] md:text-xs truncate">
                        Arun Kumar
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-gray-500 text-[5px] md:text-[8px] font-bold uppercase bg-white/5 px-1 py-0.5 rounded">
                          Guest
                        </span>
                        <span className="text-orange-400 text-[5px] md:text-[8px] font-bold">
                          Unit 105
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2 shrink-0">
                      <span className="text-gray-500 text-[5px] md:text-[8px] font-mono hidden md:inline">
                        10:38 AM
                      </span>
                      <div className="px-1 md:px-2 py-0.5 rounded-md border text-[5px] md:text-[7px] font-black uppercase tracking-wider text-yellow-500 bg-yellow-500/10 border-yellow-500/20 flex items-center gap-0.5">
                        <Timer size={6} className="md:w-2 md:h-2" />{" "}
                        <span className="hidden md:inline">Pending</span>
                      </div>
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div className="flex items-center gap-1 md:gap-3 p-1 md:p-2.5 rounded-md md:rounded-xl hover:bg-[#1A1A1A] border border-transparent hover:border-white/5 transition-colors group">
                    <div className="w-2.5 h-2.5 md:w-4 md:h-4 rounded-sm md:rounded-md border-2 border-gray-600 group-hover:border-gray-400 transition-colors shrink-0">
                      <Square
                        size={6}
                        className="opacity-0 md:w-2.5 md:h-2.5"
                      />
                    </div>
                    <div className="w-4 h-4 md:w-8 md:h-8 rounded-md md:rounded-lg bg-[#1A1A1A] flex items-center justify-center border border-white/5 shrink-0">
                      <Car size={6} className="text-gray-400 md:w-4 md:h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-[6px] md:text-xs truncate">
                        Uber - MH04 AB 1234
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-gray-500 text-[5px] md:text-[8px] font-bold uppercase bg-white/5 px-1 py-0.5 rounded">
                          Cab
                        </span>
                        <span className="text-orange-400 text-[5px] md:text-[8px] font-bold">
                          Unit 501
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2 shrink-0">
                      <span className="text-gray-500 text-[5px] md:text-[8px] font-mono hidden md:inline">
                        10:35 AM
                      </span>
                      <div className="px-1 md:px-2 py-0.5 rounded-md border text-[5px] md:text-[7px] font-black uppercase tracking-wider text-blue-400 bg-blue-500/10 border-blue-500/20 flex items-center gap-0.5">
                        <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" />{" "}
                        <span className="hidden md:inline">Inside</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* === CASH FLOW AREA CHART (CSS/SVG Visual) === */}
              <div className="h-16 md:h-28 bg-[#111] rounded-lg md:rounded-2xl border border-white/5 p-1.5 md:p-4 relative z-10 overflow-hidden">
                <div className="flex items-center justify-between mb-1 md:mb-2">
                  <div className="flex items-center gap-1 md:gap-2">
                    <IndianRupee
                      size={8}
                      className="text-green-500 md:w-4 md:h-4"
                    />
                    <span className="text-white font-black text-[6px] md:text-xs uppercase tracking-wider">
                      Cash Flow
                    </span>
                  </div>
                  <div className="flex items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-0.5 md:gap-1">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full" />
                      <span className="text-[5px] md:text-[8px] text-gray-500 font-bold">
                        Income
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5 md:gap-1">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-red-500 rounded-full" />
                      <span className="text-[5px] md:text-[8px] text-gray-500 font-bold">
                        Expense
                      </span>
                    </div>
                  </div>
                </div>

                {/* SVG Area Chart */}
                <div className="relative h-8 md:h-16 w-full">
                  <svg
                    viewBox="0 0 200 40"
                    className="w-full h-full"
                    preserveAspectRatio="none"
                  >
                    {/* Income Area */}
                    <defs>
                      <linearGradient
                        id="incomeGrad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#22C55E"
                          stopOpacity="0.3"
                        />
                        <stop
                          offset="100%"
                          stopColor="#22C55E"
                          stopOpacity="0"
                        />
                      </linearGradient>
                      <linearGradient
                        id="expenseGrad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#EF4444"
                          stopOpacity="0.3"
                        />
                        <stop
                          offset="100%"
                          stopColor="#EF4444"
                          stopOpacity="0"
                        />
                      </linearGradient>
                    </defs>
                    {/* Income Path */}
                    <path
                      d="M0,35 C20,30 40,20 60,22 C80,24 100,15 120,12 C140,9 160,18 180,14 C190,12 195,10 200,8 L200,40 L0,40 Z"
                      fill="url(#incomeGrad)"
                    />
                    <path
                      d="M0,35 C20,30 40,20 60,22 C80,24 100,15 120,12 C140,9 160,18 180,14 C190,12 195,10 200,8"
                      fill="none"
                      stroke="#22C55E"
                      strokeWidth="1.5"
                    />
                    {/* Expense Path */}
                    <path
                      d="M0,38 C20,36 40,30 60,32 C80,34 100,28 120,26 C140,24 160,30 180,28 C190,27 195,25 200,22 L200,40 L0,40 Z"
                      fill="url(#expenseGrad)"
                    />
                    <path
                      d="M0,38 C20,36 40,30 60,32 C80,34 100,28 120,26 C140,24 160,30 180,28 C190,27 195,25 200,22"
                      fill="none"
                      stroke="#EF4444"
                      strokeWidth="1.5"
                    />
                  </svg>
                  {/* X-Axis Labels */}
                  <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[5px] md:text-[7px] text-gray-600 font-mono uppercase px-1">
                    <span>Aug</span>
                    <span>Sep</span>
                    <span>Oct</span>
                    <span>Nov</span>
                    <span>Dec</span>
                    <span>Jan</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContainerScroll>
      </div>

      {/* --- LEAD GEN MODAL POPUP --- */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-100 flex items-center justify-center px-4">
            {/* 1. Backdrop (Click to close) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />

            {/* 2. The Modal Card */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="demo-form-card bg-[#111] border border-white/10 p-6 md:p-8 rounded-4xl shadow-2xl relative z-10 w-full max-w-lg"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-white p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <h2 className="text-2xl font-bold text-white mb-2">
                Get Protected
              </h2>
              <p className="text-sm text-gray-400 mb-6">
                Secure your society with Smart Dwara today.
              </p>

              <form className="space-y-4" onSubmit={handleSubmit}>
                {/* Row 1: Name & Mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Name
                    </label>
                    <div className="relative">
                      <Users className="w-4 h-4 absolute left-3 top-3.5 text-gray-600" />
                      <input
                        suppressHydrationWarning
                        type="text"
                        placeholder="Enter Name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Mobile
                    </label>
                    <div className="relative">
                      <Smartphone className="w-4 h-4 absolute left-3 top-3.5 text-gray-600" />
                      <input
                        suppressHydrationWarning
                        type="tel"
                        placeholder="Mobile Number"
                        value={formData.mobile}
                        onChange={(e) =>
                          setFormData({ ...formData, mobile: e.target.value })
                        }
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Row 2: Email */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Email ID
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-3.5 text-gray-600" />
                    <input
                      suppressHydrationWarning
                      type="email"
                      placeholder="Enter Email Address"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                {/* Row 3: City */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    City
                  </label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 absolute left-3 top-3.5 text-gray-600" />
                    <input
                      suppressHydrationWarning
                      type="text"
                      placeholder="Enter City"
                      value={formData.city}
                      onChange={(e) =>
                        setFormData({ ...formData, city: e.target.value })
                      }
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                {/* Row 4: Interest */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    I am interested in...
                  </label>
                  <select
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                    value={formData.interest}
                    onChange={(e) =>
                      setFormData({ ...formData, interest: e.target.value })
                    }
                  >
                    <option>RWA Solutions</option>
                    <option>Individual Home Security</option>
                    <option>Commercial Access Control</option>
                  </select>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-[#F97316] hover:bg-orange-600 disabled:bg-orange-800 disabled:cursor-not-allowed text-white font-bold uppercase rounded-xl transition-all shadow-lg shadow-orange-900/20 flex items-center justify-center gap-2 mt-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Book a demo <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default HeroSection;
